import { inject, Injectable, signal } from '@angular/core';
import {
  PaymentPlain,
  Payment,
  Channel,
  CouponCashing,
  PaymentproviderShort,
  PaymentproviderTypeAmazonId,
  paymentproviderTypes,
  OrderContent,
  PaymentproviderTypePaypalGooglePayId,
  PaymentproviderTypePaypalApplePayId
} from '@frontend/masterdata';
import { ReplaySubject } from 'rxjs';
import { IMAGE_URL, HttpHelper, QueryParser } from '@frontend/basic';
import { ViewState, QueryParams, PaymentService } from '@frontend/frontendlib';

@Injectable({
  providedIn: 'root',
})
export class ParameterService extends HttpHelper 
{

  $fininishedLoading = new ReplaySubject<boolean>();
  
  queryParams: QueryParams = {
    channel_id: ''
  }

  public imgUrl = inject(IMAGE_URL);

  channel: Channel | undefined;
  payment: Payment | undefined;
  download: boolean | undefined;
  coupon_input = false;

  coupon_cashing_list = [] as CouponCashing[];
  serverdate: string | undefined;

  loading = false;
  list = [] as PaymentproviderShort[];

  state = ViewState.Init;

  paymentproviderTypes = paymentproviderTypes;

  private paymentService = inject(PaymentService);
  public choosedPaymentprovider = signal({} as PaymentproviderShort);
  
  constructor() {
    super();
    this.$fininishedLoading.next(false);
  }

  async init() {
    this.queryParams = QueryParser.parseQueryParams();
    this.load_data();
  }

  init_state() {
    this.state = this.queryParams.paymentprovider_id === undefined ? 1 : 2;
    
    if (this.queryParams.error === '1') {
      this.state = ViewState.Choosepayment;
    }
    if (this.queryParams.seccode !== undefined) {
      this.state = ViewState.Ready;
    }

    if (this.payment && this.payment.state * 1 === 2) {
      this.state = ViewState.Ready;
    }

    if (this.payment && this.payment.state * 1 === 3) {
      this.state = ViewState.Open;
    }

    if (this.queryParams.checksuccess === '1') {
      this.state = ViewState.CheckSuccess;
    }

    if (this.queryParams.showamazon === '1' || this.queryParams.showamazon === '2') {
      this.state = ViewState.ShowAmazon;
    }
  }

  mapPlainPayment(ppayment: PaymentPlain): Payment {
    let ordercontent: OrderContent = { items: [] };
    let totalamount = 0;
    try {
      ordercontent = JSON.parse(
        decodeURIComponent(ppayment.ordercontent.replace(/\+/g, ' '))
      );
      totalamount = ordercontent.items.reduce(
        (prev, item) => prev + item.amount * item.count,
        0
      );
    } catch {
      //not parseable
    }
    return {
      ...ppayment,
      ordercontent,
      totalamount,
    };
  }

  load_data() {
    this.loading = true;
    this.paymentService.getPayment(this.queryParams.channel_id, this.queryParams.payment_id ?? '')
      .subscribe((val) => {
        this.payment = this.mapPlainPayment(val['payment'] as PaymentPlain);
        this.channel = val.channel;
        this.coupon_cashing_list = val.coupon_cashing ?? [];
        this.download = val.download;
        this.coupon_cashing_list = this.coupon_cashing_list ?? [];
        this.list = val.list;
        this.serverdate = val.serverdate;

        for (const el of this.list) {
          this.updateImageForPaymentprovider(el);
          if (el.id === this.queryParams.paymentprovider_id) {
            this.choosedPaymentprovider.set(el);
          }
        }

        if (this.channel.coupon * 1 === 1) {
          this.list.push(this.getCouponAsPaymentprovider());
        }

        if (this.getOpenAmount() <= 0) {
          this.state = 4;
        }

        this.loading = false;
        this.$fininishedLoading.next(true);
        this.$fininishedLoading.complete();
      });
  }

  getOpenAmount(): number {
    return this.coupon_cashing_list.reduce(
      (prev: number, val: CouponCashing) => {
        return prev - val.amount;
      },
      (this.payment?.amount ?? 0) * 1
    );
  }

  runGoogleTag(gtagType: string) {

    if (
      this.windowRef['gtag'] &&
      typeof this.windowRef['gtag'] === 'function' &&
      (this.payment?.ordercontent.items.length ?? 0) > 0
    ) {
      this.windowRef['gtag']('event', gtagType, {
        value: this.getGaAmount(),
        currency: 'EUR',
        transaction_id: this.payment?.public_id,
        items: this.getGaItems(),
      });
    }
  }

  // eslint-disable-next-line
  get windowRef(): any {
    return window;
  }

  getGaAmount(): number {
    return this.payment ? this.payment.ordercontent.items.reduce(
      (prev, val) => prev + val.amount * val.count,
      0
    ) : 0;
  }

  getGaItems() {
    return this.payment ? this.payment.ordercontent.items.map((val) => {
      return {
        item_id: val.id,
        item_name: val.name,
        price: val.amount,
        quantity: val.count,
      };
    }) : 0;
  }

  private isInList(type: number): boolean
  {
    return this.list.find(
        (val) =>
          val.id === this.queryParams.paymentprovider_id &&
          val.type === type
      ) !== undefined;
  }

  isAmazon() { return this.isInList(PaymentproviderTypeAmazonId); }
  isGooglepayOverPaypal() { return this.isInList(PaymentproviderTypePaypalGooglePayId); }
  isApplePayOverPaypal() { return this.isInList(PaymentproviderTypePaypalApplePayId); }

  getAllQueryParameter() {
    return this.getAllParamsWithFilter([
      'channel',
      'payment',
      'paymentprovider'
    ]);
  }

  getAllQueryParameterWithOrigin()
  {
    return {
      ...this.getAllQueryParameter(),
      origin: this.getOriginPath()
    }
  }

  private getCouponAsPaymentprovider(): PaymentproviderShort {
    const dateFrom = new Date(this.serverdate ?? '');
    const dateTo = new Date(this.payment?.expired_until.date ?? '');
    const diff = (dateTo.getTime() - dateFrom.getTime()) / 1000;
    return {
      id: 'coupon',
      name: 'Gutschein / Rabattcode',
      hint: '',
      imgsrc: `${this.imgUrl}/assets/img/coupon.png`,
      imgheight: 30,
      type: 0,
      active: diff >= 0,
      error: diff < 0 ? 'mindurationerror' : undefined,
      min_duration: 0,
      min_amount: 0,
      description: this.channel?.coupontext ?? 'Bezahlen per Gutschein',
      response_text: '',
      response_header: ''
    };
  }

  private updateImageForPaymentprovider(paymentProvider: PaymentproviderShort): void {
    const pp = this.paymentproviderTypes.find(
      (val) => parseInt(val.id) * 1 === paymentProvider.type * 1
    );
    if (pp) 
    {
      paymentProvider.name = pp.name;
      paymentProvider.imgsrc = `${this.imgUrl}/assets/img/${pp.img}`;
      paymentProvider.imgheight = pp.imgheight;
      paymentProvider.response_header = paymentProvider.response_header === '' ? undefined : paymentProvider.response_header;
      paymentProvider.response_text = paymentProvider.response_text === ''? undefined : paymentProvider.response_text;
    }
  }

  private getPort(): string
  {
    return window.location.port === '80' || window.location.port === '443' ? '' : `:${window.location.port}`;
  }

  public getOriginPath(): string
  {
    return `${window.location.protocol}//${window.location.hostname}${this.getPort()}${window.location.pathname}`;
  }

  public getOverviewUrl(): string
  {
    return `${window.location.pathname}?channel=${this.queryParams.channel_id}&payment=${this.queryParams.payment_id}`;
  }
}
